'use client';

import { BubbleBackground, Card, cn, ShineBorder } from '@repo/ui';
import { motion, useReducedMotion } from 'motion/react';
import type * as React from 'react';
import {
  cardContainerVariants,
  cardWidthVariants,
  createGridVariants,
  useDesktop,
  videoVariants,
} from './helper';
import { LoginCardContent } from './login-card-content';
import { LoginVideo } from './login-video';
import { TiltCard } from './tilt-card';

interface ILoginTemplateProps extends React.ComponentProps<'div'> {
  errorMessage?: string | null;
  onProviderStart?: (method: string) => void;
}

export function LoginTemplate({
  className,
  errorMessage,
  onProviderStart,
  ...props
}: ILoginTemplateProps): React.ReactNode {
  const prefersReducedMotion = useReducedMotion();
  const { isDesktop, mounted } = useDesktop();
  const gridVariants = createGridVariants(
    isDesktop ?? false,
    mounted ?? false,
    prefersReducedMotion ?? false,
  );

  return (
    <BubbleBackground
      className="flex min-h-svh flex-col items-center justify-center p-6 md:p-10"
      interactive
    >
      <motion.div
        className="w-full max-w-sm"
        initial="narrow"
        animate={mounted && isDesktop ? 'wide' : 'narrow'}
        variants={cardWidthVariants}
      >
        <div className={cn('flex flex-col gap-6 select-none', className)} {...props}>
          <motion.div
            initial="hidden"
            animate="visible"
            variants={cardContainerVariants}
            className="w-full"
          >
            <TiltCard>
              <Card className="relative overflow-hidden p-0 backdrop-blur-md bg-white/20 dark:bg-black/40 border-0 shadow-lg shadow-white/10">
                <ShineBorder
                  borderWidth={1}
                  duration={14}
                  shineColor={['#ffffff', '#ffffff40', '#ffffff']}
                />
                <motion.div
                  className="grid p-0 md:min-h-[320px]"
                  initial="singleColumn"
                  animate="doubleColumn"
                  variants={gridVariants}
                >
                  <LoginCardContent errorMessage={errorMessage} onProviderStart={onProviderStart} />
                  {isDesktop && (
                    <motion.div
                      className="hidden md:block overflow-hidden"
                      variants={videoVariants}
                      initial="hidden"
                      animate="visible"
                    >
                      <LoginVideo className="w-full h-full object-cover" />
                    </motion.div>
                  )}
                </motion.div>
              </Card>
            </TiltCard>
          </motion.div>
        </div>
      </motion.div>
    </BubbleBackground>
  );
}
